import { jsPDF } from 'jspdf';
import { formatDate, type FinanceInvoice, type FinanceInvoiceItem } from '@/lib/finance';
import type { CompanyProfile } from '@/lib/companyProfile';

// ─────────────────────────────────────────────────────────────
// Génération PDF des factures et devis, structurée en 5 blocs :
// vendeur (droite) · client (gauche) · références · lignes · totaux
//
// ⚠️ Les polices PDF standard (Helvetica) ne savent pas afficher
// l'espace fine insécable produite par toLocaleString('fr-FR') :
// elle s'imprimait comme un « / » (ex. « 2 /223 MAD »).
// On formate donc les montants nous-mêmes avec une espace normale.
// ─────────────────────────────────────────────────────────────

/** Montant PDF-safe : 1223.5 → « 1 223,50 MAD » (espace simple). */
function money(amount: number, currency = 'MAD'): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const negative = value < 0;
  const abs = Math.abs(value);
  const fixed = abs.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const decimals = decPart === '00' ? '' : `,${decPart}`;
  return `${negative ? '-' : ''}${grouped}${decimals} ${currency}`;
}

/** Nettoie toute chaîne avant écriture PDF (espaces exotiques). */
function safe(text: string | null | undefined): string {
  return String(text ?? '')
    .replace(/[\u202f\u00a0\u2009\u2007]/g, ' ')
    .replace(/[\u2011\u2013\u2014]/g, '-');
}

export function buildInvoicePdf(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): jsPDF {
  const isQuote = invoice.doc_type === 'quote';
  const cur = invoice.currency || 'MAD';
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  const midX = W / 2 + 6;
  const rightX = W - M;

  // ── Bandeau supérieur : titre à gauche, vendeur à DROITE ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(20);
  doc.text(isQuote ? 'DEVIS' : 'FACTURE', M, 58);

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(90);
  doc.text(safe(`N° ${invoice.number}`), M, 74);

  // Bloc vendeur (aligné à droite)
  doc.setTextColor(120);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('EMETTEUR', rightX, 24, { align: 'right' });

  doc.setTextColor(20);
  doc.setFontSize(12);
  doc.text(safe(profile.company_name || 'Mon entreprise'), rightX, 40, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  doc.setTextColor(95);
  let sy = 54;
  const sellerLines: string[] = [];
  if (profile.legal_form) sellerLines.push(profile.legal_form);
  if (profile.address) sellerLines.push(profile.address);
  const cityLine = [profile.postal_code, profile.city].filter(Boolean).join(' ');
  if (cityLine) sellerLines.push(cityLine);
  if (profile.country) sellerLines.push(profile.country);
  if (profile.phone) sellerLines.push(`Tel : ${profile.phone}`);
  if (profile.email) sellerLines.push(profile.email);
  sellerLines.forEach((line) => {
    doc.text(safe(line), rightX, sy, { align: 'right' });
    sy += 10;
  });

  const idParts: string[] = [];
  if (profile.ice) idParts.push(`ICE : ${profile.ice}`);
  if (profile.if_number) idParts.push(`IF : ${profile.if_number}`);
  if (profile.rc_number) idParts.push(`RC : ${profile.rc_number}`);
  if (profile.tp_number) idParts.push(`TP : ${profile.tp_number}`);
  doc.setTextColor(60);
  idParts.forEach((line) => {
    doc.text(safe(line), rightX, sy, { align: 'right' });
    sy += 10;
  });

  // ── Bloc client à GAUCHE, références à droite ──
  let y = Math.max(sy + 12, 100);

  const clientLines: string[] = [];
  if (invoice.customer_name) clientLines.push(invoice.customer_name);
  if (invoice.customer_address) clientLines.push(invoice.customer_address);
  if (invoice.customer_phone) clientLines.push(`Tel : ${invoice.customer_phone}`);
  if (invoice.customer_email) clientLines.push(invoice.customer_email);
  if (invoice.customer_ice) clientLines.push(`ICE : ${invoice.customer_ice}`);

  const boxH = 22 + clientLines.length * 11;
  const boxW = W / 2 - M - 14;
  doc.setFillColor(248, 248, 248);
  doc.setDrawColor(228, 228, 228);
  doc.roundedRect(M, y, boxW, boxH, 4, 4, 'FD');

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120);
  doc.text(isQuote ? 'CLIENT / PROSPECT' : 'FACTURE A', M + 10, y + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.6);
  doc.setTextColor(25);
  let cy = y + 24;
  clientLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(safe(line), boxW - 20) as string[];
    doc.text(wrapped[0] || '', M + 10, cy);
    cy += 11;
  });

  // Références (colonne de droite)
  let ry = y + 12;
  doc.setFontSize(8.2);
  doc.setTextColor(95);
  const refs: [string, string][] = [];
  refs.push(['Date d emission', formatDate(invoice.issue_date)]);
  if (invoice.sale_date) refs.push(['Date de la vente', formatDate(invoice.sale_date)]);
  if (invoice.due_date) refs.push([isQuote ? 'Validite' : 'Echeance', formatDate(invoice.due_date)]);
  if (invoice.reference) refs.push(['Reference', invoice.reference]);
  refs.forEach(([label, value]) => {
    doc.setTextColor(130);
    doc.text(safe(label), midX, ry);
    doc.setTextColor(25);
    doc.text(safe(value), rightX, ry, { align: 'right' });
    ry += 11;
  });

  y = Math.max(y + boxH, ry) + 18;

  // ── Tableau des lignes ──
  const cDesc = M + 6;
  const cQty = W - 292;
  const cPu = W - 218;
  const cRem = W - 145;
  const cTot = W - M - 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(60);
  doc.setFillColor(238, 238, 238);
  doc.rect(M, y - 11, W - M * 2, 17, 'F');
  doc.text('Designation', cDesc, y);
  doc.text('Qte', cQty, y, { align: 'right' });
  doc.text('P.U. HT', cPu, y, { align: 'right' });
  doc.text('Remise', cRem, y, { align: 'right' });
  doc.text('Total HT', cTot, y, { align: 'right' });
  y += 18;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.4);
  doc.setTextColor(25);

  items.forEach((it) => {
    if (y > H - 190) { doc.addPage(); y = 60; }
    const qty = Number(it.quantity) || 0;
    const unit = Number(it.unit_price) || 0;
    const disc = Number(it.discount) || 0;
    const net = Math.max(0, qty * unit - disc);
    const descLines = doc.splitTextToSize(safe(it.description), 218) as string[];
    doc.text(descLines[0] || '', cDesc, y);
    doc.text(String(qty), cQty, y, { align: 'right' });
    doc.text(money(unit, cur), cPu, y, { align: 'right' });
    doc.text(disc > 0 ? money(disc, cur) : '-', cRem, y, { align: 'right' });
    doc.text(money(net, cur), cTot, y, { align: 'right' });
    y += 14;
  });

  doc.setDrawColor(232, 232, 232);
  doc.line(M, y, W - M, y);
  y += 18;

  // ── Totaux ──
  if (y > H - 200) { doc.addPage(); y = 60; }
  const labelX = W - 250;
  const valueX = W - M - 6;

  doc.setFontSize(8.8);
  doc.setTextColor(60);
  const rows: [string, number][] = [['Total HT brut', Number(invoice.subtotal)]];
  if (Number(invoice.discount) > 0) rows.push(['Remise globale', -Number(invoice.discount)]);
  const taxable = Math.max(0, Number(invoice.subtotal) - Number(invoice.discount));
  if (Number(invoice.discount) > 0) rows.push(['Base taxable', taxable]);
  rows.push([
    Number(invoice.tax_rate) > 0 ? `TVA ${invoice.tax_rate} %` : 'TVA (exonere)',
    Number(invoice.tax_amount),
  ]);
  rows.forEach(([label, value]) => {
    doc.text(safe(label), labelX, y);
    doc.text(money(value, cur), valueX, y, { align: 'right' });
    y += 13;
  });

  doc.setDrawColor(190, 190, 190);
  doc.line(labelX, y - 6, valueX, y - 6);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.setTextColor(15);
  doc.text(isQuote ? 'TOTAL DEVIS TTC' : 'TOTAL TTC', labelX, y + 7);
  doc.text(money(Number(invoice.total), cur), valueX, y + 7, { align: 'right' });
  y += 32;

  // ── Modalités de paiement ──
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.2);
  doc.setTextColor(85);
  const block = (label: string, value: string | null | undefined) => {
    if (!value) return;
    const lines = doc.splitTextToSize(safe(`${label} : ${value}`), W - M * 2) as string[];
    doc.text(lines, M, y);
    y += lines.length * 10.5;
  };
  block('Conditions de paiement', invoice.payment_terms);
  block('Mode de paiement', invoice.payment_method);
  if (profile.bank_name || profile.rib) {
    block('Banque', `${profile.bank_name || '-'}${profile.rib ? ` - RIB : ${profile.rib}` : ''}`);
  }
  block('Observations', invoice.notes);
  block('Mentions', invoice.legal_mentions || profile.legal_mentions);

  if (profile.invoice_footer) {
    doc.setFontSize(7.5);
    doc.setTextColor(140);
    doc.text(safe(profile.invoice_footer), W / 2, H - 32, { align: 'center' });
  }

  return doc;
}

export function downloadInvoicePdf(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): void {
  buildInvoicePdf(invoice, items, profile).save(`${invoice.number}.pdf`);
}